import type { SVGProps, ReactNode } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  /** Visual size of the icon in pixels */
  size?: number;
};

// Shared stroke defaults - strokeWidth: 1.5 per UI consistency rules
const STROKE_DEFAULTS = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Base wrapper for stroke-based icons.
 * Provides 16px visual icon with consistent stroke styling.
 * Parent elements should provide 24px hit area via padding/sizing.
 */
const StrokeIcon = ({
  size = 16,
  viewBox = '0 0 24 24',
  children,
  ...props
}: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    {...STROKE_DEFAULTS}
    {...props}
  >
    {children}
  </svg>
);

/**
 * Base wrapper for fill-based icons.
 * Provides 16px visual icon.
 */
const FillIcon = ({
  size = 16,
  viewBox = '0 0 24 24',
  children,
  ...props
}: IconProps & { children: ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="currentColor"
    {...props}
  >
    {children}
  </svg>
);

// Path style with non-scaling-stroke
const pathStyle = { vectorEffect: 'non-scaling-stroke' as const };

// ============================================================================
// All Icons (alphabetical)
// ============================================================================

export const AllCasesIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4.30775 19.5C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H9.798L11.798 6.5H19.6923C20.1974 6.5 20.625 6.675 20.975 7.025C21.325 7.375 21.5 7.80258 21.5 8.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775ZM4.30775 18H19.6923C19.7821 18 19.8558 17.9712 19.9135 17.9135C19.9712 17.8558 20 17.7821 20 17.6923V8.30775C20 8.21792 19.9712 8.14417 19.9135 8.0865C19.8558 8.02883 19.7821 8 19.6923 8H11.1845L9.1845 6H4.30775C4.21792 6 4.14417 6.02883 4.0865 6.0865C4.02883 6.14417 4 6.21792 4 6.30775V17.6923C4 17.7821 4.02883 17.8558 4.0865 17.9135C4.14417 17.9712 4.21792 18 4.30775 18Z" fill="currentColor" />
  </svg>
);

export const ArrowLeftIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M7.373 12.75L13.0693 18.4462L12 19.5L4.5 12L12 4.5L13.0693 5.55375L7.373 11.25H19.5V12.75H7.373Z" fill="currentColor" />
  </svg>
);

export const ArrowRightIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M16.627 12.75H4.5V11.25H16.627L10.9308 5.55375L12 4.5L19.5 12L12 19.5L10.9308 18.4462L16.627 12.75Z" fill="currentColor" />
  </svg>
);

export const AutoAwesomeIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M2.19956 11.9386C5.8568 12.1591 8.78341 15.0859 9.00396 18.7431H9.03067C9.25122 15.0859 12.1778 12.1591 15.835 11.9386V11.9119C12.1778 11.6913 9.25122 8.76472 9.03067 5.10742H9.00396C8.78341 8.76472 5.8568 11.6913 2.19956 11.9119V11.9386Z" />
    <path d="M13.4645 17.0468C15.6509 17.1787 17.4005 18.9284 17.5324 21.1148H17.5484C17.6802 18.9284 19.4298 17.1787 21.6162 17.0468V17.0308C19.4298 16.899 17.6802 15.1494 17.5484 12.9629H17.5324C17.4005 15.1494 15.6509 16.899 13.4645 17.0308V17.0468Z" />
    <path d="M13.425 5.9663C15.1741 6.07178 16.5738 7.47152 16.6793 9.22061H16.6921C16.7975 7.47152 18.1972 6.07178 19.9463 5.9663V5.95353C18.1972 5.84804 16.7975 4.44836 16.6921 2.69922H16.6793C16.5738 4.44836 15.1741 5.84804 13.425 5.95353V5.9663Z" />
  </svg>
);

export const BellIcon = (props: IconProps) => (
  <StrokeIcon size={20} {...props}>
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" style={pathStyle} />
    <path d="M13.73 21a2 2 0 01-3.46 0" style={pathStyle} />
  </StrokeIcon>
);

export const BookIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M2 3h4a2 2 0 012 2v9a1.5 1.5 0 00-1.5-1.5H2V3zM14 3h-4a2 2 0 00-2 2v9a1.5 1.5 0 011.5-1.5H14V3z" style={pathStyle} />
  </StrokeIcon>
);

export const ChartIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M2 14V8h3v6H2zM6.5 14V5h3v9h-3zM11 14V2h3v12h-3z" style={pathStyle} />
  </StrokeIcon>
);

export const ChevronDownIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M12.0001 14.3077L8.19238 10.5H15.8079L12.0001 14.3077Z" />
  </FillIcon>
);

/** Stroke-based chevron (v-shape). Use for interactive selectors; reserve ChevronDownIcon (▼ caret) for accordions. */
export const ChevronDownStrokeIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <polyline points="6 9 12 15 18 9" style={pathStyle} />
  </StrokeIcon>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <polyline points="15 18 9 12 15 6" style={pathStyle} />
  </StrokeIcon>
);

export const ChevronRightIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <polyline points="9 18 15 12 9 6" style={pathStyle} />
  </StrokeIcon>
);

export const ClearHighlightingIcon = (props: IconProps) => (
  <StrokeIcon size={24} viewBox="7 8 18 18" {...props}>
    <path d="M12.6667 22L9.80005 19.1333C9.13338 18.4667 9.13338 17.4667 9.80005 16.8667L16.2 10.4667C16.8667 9.80001 17.8667 9.80001 18.4667 10.4667L22.2 14.2C22.8667 14.8667 22.8667 15.8667 22.2 16.4667L16.6667 22" style={pathStyle} />
    <path d="M22.6667 22H12.6667" style={pathStyle} />
    <path d="M11.3333 15.3333L17.3333 21.3333" style={pathStyle} />
  </StrokeIcon>
);

export const ClipboardIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M10 2H6a1 1 0 00-1 1v1h6V3a1 1 0 00-1-1z" style={pathStyle} />
    <path d="M11 4V3a2 2 0 00-2-2H7a2 2 0 00-2 2v1H4a1 1 0 00-1 1v9a1 1 0 001 1h8a1 1 0 001-1V5a1 1 0 00-1-1h-1z" style={pathStyle} />
  </StrokeIcon>
);

export const CloseIcon = (props: IconProps) => (
  <StrokeIcon size={20} viewBox="0 0 20 20" {...props}>
    <path d="M15 5L5 15M5 5L15 15" style={pathStyle} />
  </StrokeIcon>
);

export const CloseMenuIcon = (props: IconProps) => (
  <StrokeIcon size={24} {...props}>
    <path d="M11 19L4 12L11 5M19 19L12 12L19 5" style={pathStyle} />
  </StrokeIcon>
);

export const CopyIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" style={pathStyle} />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" style={pathStyle} />
  </StrokeIcon>
);

export const DocumentIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M4 2h5l4 4v8a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" style={pathStyle} />
    <path d="M9 2v4h4" style={pathStyle} />
  </StrokeIcon>
);

export const DownloadIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" style={pathStyle} />
    <polyline points="7 10 12 15 17 10" style={pathStyle} />
    <line x1="12" y1="15" x2="12" y2="3" style={pathStyle} />
  </StrokeIcon>
);

export const FolderIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M4.30775 19.5C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H9.798L11.798 6.5H19.6923C20.1974 6.5 20.625 6.675 20.975 7.025C21.325 7.375 21.5 7.80258 21.5 8.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775ZM4.30775 18H19.6923C19.7821 18 19.8558 17.9712 19.9135 17.9135C19.9712 17.8558 20 17.7821 20 17.6923V8.30775C20 8.21792 19.9712 8.14417 19.9135 8.0865C19.8558 8.02883 19.7821 8 19.6923 8H11.1845L9.1845 6H4.30775C4.21792 6 4.14417 6.02883 4.0865 6.0865C4.02883 6.14417 4 6.21792 4 6.30775V17.6923C4 17.7821 4.02883 17.8558 4.0865 17.9135C4.14417 17.9712 4.21792 18 4.30775 18Z" fill="currentColor" />
  </svg>
);

export const DSGridIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5.6155 20C5.15517 20 4.77083 19.8458 4.4625 19.5375C4.15417 19.2292 4 18.8448 4 18.3845V5.6155C4 5.15517 4.15417 4.77083 4.4625 4.4625C4.77083 4.15417 5.15517 4 5.6155 4H18.3845C18.8448 4 19.2292 4.15417 19.5375 4.4625C19.8458 4.77083 20 5.15517 20 5.6155V18.3845C20 18.8448 19.8458 19.2292 19.5375 19.5375C19.2292 19.8458 18.8448 20 18.3845 20H5.6155ZM5.6155 19H8.998V15.002H5V18.3845C5 18.564 5.05767 18.7115 5.173 18.827C5.2885 18.9423 5.436 19 5.6155 19ZM9.998 19H14.002V15.002H9.998V19ZM15.002 19H18.3845C18.564 19 18.7115 18.9423 18.827 18.827C18.9423 18.7115 19 18.564 19 18.3845V15.002H15.002V19ZM5 14.002H8.998V9.998H5V14.002ZM9.998 14.002H14.002V9.998H9.998V14.002ZM15.002 14.002H19V9.998H15.002V14.002ZM5 8.998H8.998V5H5.6155C5.436 5 5.2885 5.05767 5.173 5.173C5.05767 5.2885 5 5.436 5 5.6155V8.998ZM9.998 8.998H14.002V5H9.998V8.998ZM15.002 8.998H19V5.6155C19 5.436 18.9423 5.2885 18.827 5.173C18.7115 5.05767 18.564 5 18.3845 5H15.002V8.998Z" />
  </svg>
);

export const ExpandDiagonalIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M17.5858 5H14V3H21V10H19V6.41421L14.7071 10.7071L13.2929 9.29289L17.5858 5ZM3 14H5V17.5858L9.29289 13.2929L10.7071 14.7071L6.41421 19H10V21H3V14Z" />
  </FillIcon>
);

export const ExpandIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3.5 20.5V13H5V17.9557L17.9557 5H13V3.5H20.5V11H19V6.04425L6.04425 19H11V20.5H3.5Z" />
  </svg>
);

export const FileTextIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M4 2h5l4 4v8a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" style={pathStyle} />
    <path d="M9 2v4h4" style={pathStyle} />
    <path d="M5 9h6M5 12h4" style={pathStyle} />
  </StrokeIcon>
);

export const GeneratedAnalysisIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M13 3.99984H11V8.99985C11 9.55214 10.5523 9.99985 10 9.99985H5V19.9999H19V9.99985H21V21.0077C21 21.5553 20.5552 21.9997 20.0068 21.9999H3.99316C3.44464 21.9997 3.00001 21.5498 3 20.993V7.99985L9 2.00277V1.99984H13V3.99984ZM5.8291 7.99985H9V4.8309L5.8291 7.99985ZM19.4707 0.329353C19.2943 -0.0964437 18.7059 -0.0964324 18.5293 0.329353L18.2764 0.940682C17.8445 1.9835 17.0385 2.8162 16.0254 3.26686L15.3076 3.5862C14.8974 3.76901 14.8975 4.36623 15.3076 4.54909L16.0674 4.88698C17.0552 5.32631 17.8476 6.12933 18.2871 7.13796L18.5332 7.70339C18.7137 8.11753 19.2864 8.11753 19.4668 7.70339L19.7139 7.13796C20.1534 6.12949 20.945 5.32626 21.9326 4.88698L22.6924 4.54909C23.1026 4.36622 23.1027 3.76902 22.6924 3.5862L21.9746 3.26686C20.9616 2.8162 20.1556 1.98348 19.7237 0.940682L19.4707 0.329353Z" />
  </FillIcon>
);

export const GeneratedTablesIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M14 10H10V14H14V10ZM16 10V14H19V10H16ZM14 19V16H10V19H14ZM16 19H19V16H16V19ZM14 5H10V8H14V5ZM16 5V8H19V5H16ZM8 10H5V14H8V10ZM8 19V16H5V19H8ZM8 5H5V8H8V5ZM4 3H20C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4C3 3.44772 3.44772 3 4 3Z" />
  </FillIcon>
);

export const GridIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <rect x="2" y="2" width="5" height="5" rx="1" style={pathStyle} />
    <rect x="9" y="2" width="5" height="5" rx="1" style={pathStyle} />
    <rect x="2" y="9" width="5" height="5" rx="1" style={pathStyle} />
    <rect x="9" y="9" width="5" height="5" rx="1" style={pathStyle} />
  </StrokeIcon>
);

export const GroupAddIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.6924 11.3348C13.0604 10.9426 13.319 10.4882 13.4684 9.97152C13.6177 9.45485 13.6924 8.92593 13.6924 8.38477C13.6924 7.84377 13.6177 7.31493 13.4684 6.79827C13.319 6.2816 13.0604 5.8271 12.6924 5.43477C13.4489 5.5041 14.0739 5.82077 14.5674 6.38477C15.061 6.94893 15.3079 7.6156 15.3079 8.38477C15.3079 9.1541 15.061 9.82077 14.5674 10.3848C14.0739 10.9489 13.4489 11.2656 12.6924 11.3348ZM17.6924 18.6158V16.9233C17.6924 16.4329 17.604 15.9664 17.4271 15.5238C17.2501 15.0811 16.9706 14.7156 16.5886 14.4273C17.272 14.6374 17.896 14.9383 18.4606 15.33C19.0255 15.7217 19.3079 16.2528 19.3079 16.9233V18.6158H17.6924ZM19.3079 12.5003V10.5003H17.3079V9.50027H19.3079V7.50027H20.3079V9.50027H22.3079V10.5003H20.3079V12.5003H19.3079ZM6.56938 10.508C5.98472 9.92335 5.69238 9.2156 5.69238 8.38477C5.69238 7.5541 5.98472 6.84643 6.56938 6.26177C7.15405 5.6771 7.86172 5.38477 8.69238 5.38477C9.52322 5.38477 10.231 5.6771 10.8156 6.26177C11.4001 6.84643 11.6924 7.5541 11.6924 8.38477C11.6924 9.2156 11.4001 9.92335 10.8156 10.508C10.231 11.0925 9.52322 11.3848 8.69238 11.3848C7.86172 11.3848 7.15405 11.0925 6.56938 10.508ZM1.69238 18.6158V16.9695C1.69238 16.5567 1.81255 16.1704 2.05288 15.8108C2.29338 15.4513 2.6168 15.1721 3.02313 14.9733C3.9668 14.5208 4.91105 14.1813 5.85588 13.955C6.80072 13.7288 7.74622 13.6158 8.69238 13.6158C9.63855 13.6158 10.584 13.7288 11.5289 13.955C12.4739 14.1813 13.4181 14.5208 14.3616 14.9733C14.7681 15.1721 15.0916 15.4513 15.3319 15.8108C15.5722 16.1704 15.6924 16.5567 15.6924 16.9695V18.6158H1.69238ZM8.69238 10.3848C9.24238 10.3848 9.71322 10.1889 10.1049 9.79727C10.4965 9.4056 10.6924 8.93477 10.6924 8.38477C10.6924 7.83477 10.4965 7.36393 10.1049 6.97227C9.71322 6.5806 9.24238 6.38477 8.69238 6.38477C8.14238 6.38477 7.67155 6.5806 7.27988 6.97227C6.88822 7.36393 6.69238 7.83477 6.69238 8.38477C6.69238 8.93477 6.88822 9.4056 7.27988 9.79727C7.67155 10.1889 8.14238 10.3848 8.69238 10.3848ZM2.69238 17.6158H14.6924V16.9695C14.6924 16.7477 14.621 16.5393 14.4781 16.3445C14.3351 16.1497 14.1373 15.9849 13.8846 15.8503C13.0616 15.4516 12.213 15.1462 11.3389 14.934C10.4647 14.7218 9.58255 14.6158 8.69238 14.6158C7.80238 14.6158 6.9203 14.7218 6.04613 14.934C5.1718 15.1462 4.32313 15.4516 3.50013 15.8503C3.24763 15.9849 3.04988 16.1497 2.90688 16.3445C2.76388 16.5393 2.69238 16.7477 2.69238 16.9695V17.6158Z" />
  </svg>
);

export const HideUpdateHighlightsIcon = (props: IconProps) => (
  <StrokeIcon size={24} viewBox="7 8 18 18" {...props}>
    <path d="M14 15.3333L10 19.3333V21.3333H16L18 19.3333" style={pathStyle} />
    <path d="M22.6666 16L19.6 19.0667C19.3507 19.311 19.0156 19.4478 18.6666 19.4478C18.3176 19.4478 17.9825 19.311 17.7333 19.0667L14.2666 15.6C14.0223 15.3508 13.8855 15.0157 13.8855 14.6667C13.8855 14.3177 14.0223 13.9826 14.2666 13.7334L17.3333 10.6667" style={pathStyle} />
  </StrokeIcon>
);

export const HistoryIcon = (props: IconProps) => (
  <StrokeIcon size={24} {...props}>
    <path d="M12 8v4l3 3" style={pathStyle} />
    <circle cx="12" cy="12" r="9" style={pathStyle} />
  </StrokeIcon>
);

export const AttainReportIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.6155 21C6.15517 21 5.77083 20.8458 5.4625 20.5375C5.15417 20.2292 5 19.8448 5 19.3845V4.6155C5 4.15517 5.15417 3.77083 5.4625 3.4625C5.77083 3.15417 6.15517 3 6.6155 3H14.5L19 7.5V12H18V8H14V4H6.6155C6.4615 4 6.32042 4.06408 6.19225 4.19225C6.06408 4.32042 6 4.4615 6 4.6155V19.3845C6 19.5385 6.06408 19.6796 6.19225 19.8078C6.32042 19.9359 6.4615 20 6.6155 20H12V21H6.6155Z" />
    <path d="M17.5 22L15.15 19.2L16.25 18.35L17.5 19.85L20.75 16L21.85 16.85L17.5 22Z" />
  </svg>
);

export const InspectPanelIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M21 3C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H21ZM20 5H13V19H20V5ZM11 5H4V19H11V5Z" />
  </FillIcon>
);

export const KebabIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <circle cx="12" cy="5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="19" r="1.5" />
  </FillIcon>
);

/**
 * Ellipsis — the same three dots as {@link KebabIcon}, laid horizontally.
 *
 * Identical geometry, turned: r=1.5 on the 24 grid at the same 7-unit pitch,
 * so the two read as one mark in two orientations rather than as two marks.
 * Which one to reach for is about the SPACE, not the meaning: the kebab suits
 * a menu hanging off the end of a row, the ellipsis a narrow vertical lane or
 * anywhere a stacked mark would read as a column of its own.
 */
export const EllipsisIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </FillIcon>
);

export const LayersIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M8 1L1 5l7 4 7-4-7-4z" style={pathStyle} />
    <path d="M1 11l7 4 7-4" style={pathStyle} />
    <path d="M1 8l7 4 7-4" style={pathStyle} />
  </StrokeIcon>
);

export const LinkIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M6.5 9.5a3 3 0 004.24 0l2.12-2.12a3 3 0 00-4.24-4.24L7.5 4.26" style={pathStyle} />
    <path d="M9.5 6.5a3 3 0 00-4.24 0L3.14 8.62a3 3 0 004.24 4.24l1.12-1.12" style={pathStyle} />
  </StrokeIcon>
);

export const ListIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <path d="M5 4h9M5 8h9M5 12h9M2 4h.01M2 8h.01M2 12h.01" style={pathStyle} />
  </StrokeIcon>
);

export const LogOutIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" style={pathStyle} />
    <polyline points="16 17 21 12 16 7" style={pathStyle} />
    <line x1="21" y1="12" x2="9" y2="12" style={pathStyle} />
  </StrokeIcon>
);

export const MaximiseIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <polyline points="4 9 4 4 9 4" style={pathStyle} />
    <line x1="4" y1="4" x2="10" y2="10" style={pathStyle} />
    <polyline points="15 4 20 4 20 9" style={pathStyle} />
    <line x1="20" y1="4" x2="14" y2="10" style={pathStyle} />
    <polyline points="4 15 4 20 9 20" style={pathStyle} />
    <line x1="4" y1="20" x2="10" y2="14" style={pathStyle} />
    <polyline points="20 15 20 20 15 20" style={pathStyle} />
    <line x1="20" y1="20" x2="14" y2="14" style={pathStyle} />
  </StrokeIcon>
);

export const OpenMenuIcon = (props: IconProps) => (
  <StrokeIcon size={24} {...props}>
    <path d="M13 19L20 12L13 5M5 19L12 12L5 5" style={pathStyle} />
  </StrokeIcon>
);

export const PriorArtIcon = (props: IconProps) => (
  <FillIcon {...props}>
    <path d="M12 16H8V8H12C14.2091 8 16 9.79086 16 12C16 14.2091 14.2091 16 12 16ZM10 10V14H12C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10H10ZM15 4H5V20H19V8H15V4ZM3 2.9918C3 2.44405 3.44749 2 3.9985 2H16L20.9997 7L21 20.9925C21 21.5489 20.5551 22 20.0066 22H3.9934C3.44476 22 3 21.5447 3 21.0082V2.9918Z" />
  </FillIcon>
);

export const RefreshIcon = (props: IconProps) => (
  <StrokeIcon size={20} {...props}>
    <path d="M21 12a9 9 0 11-2.636-6.364" style={pathStyle} />
    <path d="M21 3v6h-6" style={pathStyle} />
  </StrokeIcon>
);

export const SearchIcon = (props: IconProps) => (
  <StrokeIcon viewBox="0 0 16 16" {...props}>
    <circle cx="7" cy="7" r="4.5" style={pathStyle} />
    <path d="M10.5 10.5L14 14" style={pathStyle} />
  </StrokeIcon>
);

export const SettingsIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <circle cx="12" cy="12" r="3" style={pathStyle} />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" style={pathStyle} />
  </StrokeIcon>
);

export const ShowRedLinesIcon = (props: IconProps) => (
  <StrokeIcon size={24} viewBox="7 8 18 18" {...props}>
    <path d="M18.6667 10.6667H14.0001C13.68 10.6665 13.3646 10.7432 13.0803 10.8902C12.796 11.0372 12.5512 11.2503 12.3663 11.5116C12.1815 11.7728 12.062 12.0746 12.018 12.3917C11.974 12.7087 12.0067 13.0316 12.1134 13.3334" style={pathStyle} />
    <path d="M17.3333 16C18.0406 16 18.7189 16.281 19.219 16.781C19.719 17.2811 20 17.9594 20 18.6667C20 19.3739 19.719 20.0522 19.219 20.5523C18.7189 21.0524 18.0406 21.3333 17.3333 21.3333H12" style={pathStyle} />
    <path d="M10.6667 16H21.3334" style={pathStyle} />
  </StrokeIcon>
);

export const SidepanelIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" style={pathStyle} />
    <line x1="9" y1="3" x2="9" y2="21" style={pathStyle} />
  </StrokeIcon>
);

export const SplitIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M20.5 18.6923C20.5 19.1974 20.325 19.625 19.975 19.975C19.625 20.325 19.1974 20.5 18.6923 20.5H5.30775C4.80258 20.5 4.375 20.325 4.025 19.975C3.675 19.625 3.5 19.1974 3.5 18.6923L3.5 5.30775C3.5 4.80258 3.675 4.375 4.025 4.025C4.375 3.675 4.80258 3.5 5.30775 3.5H18.6923C19.1974 3.5 19.625 3.675 19.975 4.025C20.325 4.375 20.5 4.80258 20.5 5.30775V18.6923ZM19 18.6923V5.30775C19 5.23075 18.9679 5.16025 18.9038 5.09625C18.8398 5.03208 18.7693 5 18.6923 5L12.8077 5L12.8077 19H18.6923C18.7693 19 18.8398 18.9679 18.9038 18.9037C18.9679 18.8397 19 18.7693 19 18.6923ZM11.3077 19V5H5.30775C5.23075 5 5.16025 5.03208 5.09625 5.09625C5.03208 5.16025 5 5.23075 5 5.30775L5 18.6923C5 18.7693 5.03208 18.8397 5.09625 18.9037C5.16025 18.9679 5.23075 19 5.30775 19H11.3077Z" fill="currentColor" />
  </svg>
);

export const UsersIcon = (props: IconProps) => (
  <StrokeIcon {...props}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" style={pathStyle} />
    <circle cx="9" cy="7" r="4" style={pathStyle} />
    <path d="M23 21v-2a4 4 0 00-3-3.87" style={pathStyle} />
    <path d="M16 3.13a4 4 0 010 7.75" style={pathStyle} />
  </StrokeIcon>
);

// ============================================================================
// Aliases
// ============================================================================

export const DiamondIcon = ClearHighlightingIcon;
export const PenIcon = HideUpdateHighlightsIcon;
export const StrikethroughIcon = ShowRedLinesIcon;

/**
 * Edit (pencil-in-frame) — the inline-edit affordance on document paragraphs.
 * Material Symbols "edit" geometry (filled, 960 grid); inherits `currentColor`
 * so the caller sets the ink colour.
 */
export const EditSquareIcon = ({ size = 24, ...props }: IconProps) => (
  <FillIcon size={size} viewBox="0 -960 960 960" aria-hidden="true" focusable="false" {...props}>
    <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm280-360ZM360-360v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm481-424-56-56 56 56ZM440-440h56l232-232-28-28-29-28-231 231v57Zm260-260-29-28 29 28 28 28-28-28Z" />
  </FillIcon>
);

/**
 * Edit (pen) — the document margin rail's edit mark. A pen rather than the
 * pencil-in-frame: in a one-wide rail the frame closes up at small sizes and
 * reads as a filled square. Supplied geometry, 24 grid, `currentColor`.
 */
export const EditPenIcon = ({ size = 24, ...props }: IconProps) => (
  <FillIcon size={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path d="M5 18.8895H6.41421L15.7279 9.5758L14.3137 8.16158L5 17.4753V18.8895ZM21 20.8895H3V16.6468L16.435 3.21184C16.8256 2.82132 17.4587 2.82132 17.8492 3.21184L20.6777 6.04027C21.0682 6.43079 21.0682 7.06395 20.6777 7.45447L9.24264 18.8895H21V20.8895ZM15.7279 6.74738L17.1421 8.16158L18.5563 6.74738L17.1421 5.33316L15.7279 6.74738Z" />
  </FillIcon>
);

/** Add — the rail's plus. Supplied geometry, 24 grid, `currentColor`. */
export const PlusMarkIcon = ({ size = 24, ...props }: IconProps) => (
  <FillIcon size={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z" />
  </FillIcon>
);

/**
 * Speech bubble, OUTLINE — the comment count inside the open rail, where the
 * solid mark would fight the line icons beside it. Tail already at the lower
 * left, so it is NOT mirrored (unlike the solid bubble). Supplied geometry.
 */
export const SpeechBubbleOutlineIcon = ({ size = 24, ...props }: IconProps) => (
  <FillIcon size={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path d="M7.29117 20.8242L2 22L3.17581 16.7088C2.42544 15.3056 2 13.7025 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C10.2975 22 8.6944 21.5746 7.29117 20.8242ZM7.58075 18.711L8.23428 19.0605C9.38248 19.6745 10.6655 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 13.3345 4.32549 14.6175 4.93949 15.7657L5.28896 16.4192L4.63416 19.3658L7.58075 18.711Z" />
  </FillIcon>
);

/**
 * Speech bubble WITH A PLUS — "start a conversation here", on a paragraph that
 * has none.
 *
 * A hybrid, deliberately. The bare `+` says add-something without saying what,
 * and an empty bubble says there-is-a-conversation when the whole point is that
 * there isn't one yet. Together the noun and the verb are both present: the
 * bubble names what would be created, the plus says it does not exist yet.
 *
 * The bubble path is byte-identical to `SpeechBubbleOutlineIcon` — the same
 * mark the settled rail uses — so the two read as one family, and the plus is
 * inset (7→17 on the 24 grid, not the plus mark's own 5→19) to sit INSIDE the
 * bubble's wall rather than crossing it. Supplied geometry, `currentColor`.
 */
export const SpeechBubblePlusIcon = ({ size = 24, ...props }: IconProps) => (
  <FillIcon size={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path d="M7.29117 20.8242L2 22L3.17581 16.7088C2.42544 15.3056 2 13.7025 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C10.2975 22 8.6944 21.5746 7.29117 20.8242ZM7.58075 18.711L8.23428 19.0605C9.38248 19.6745 10.6655 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 13.3345 4.32549 14.6175 4.93949 15.7657L5.28896 16.4192L4.63416 19.3658L7.58075 18.711Z" />
    <path d="M11.2857 11.2857V7H12.7143V11.2857H17V12.7143H12.7143V17H11.2857V12.7143H7V11.2857H11.2857Z" />
  </FillIcon>
);

// Re-export types for consumers
export type { IconProps };

// ============================================================================
// Workflow Icons (subset — abstract marks tied to each Attain workflow)
// ============================================================================
// Re-exported from `./workflows/` so consumers can pull individual icons or
// the dispatcher from the top-level `@attain/ui-lib/components/icons` entry.
// Tree-shaking remains intact: only the icons you import are bundled.

export {
  NoveltyIcon,
  PatentProsecutionIcon,
  PatentDraftingIcon,
  InventionCaptureIcon,
  DocumentGenerationIcon,
  InfringementIcon,
  SepIcon,
  FtoIcon,
  WorkflowIcon,
  type WorkflowIconId,
  type WorkflowIconProps,
  type WorkflowIconBaseProps,
} from './workflows';

// Retained original stories use these pre-c35 public glyphs (44f834b63^).
export const GeneratedDocIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.6155 20C6.4615 20 6.32042 19.9359 6.19225 19.8078C6.06408 19.6796 6 19.5385 6 19.3845V4.6155C6 4.4615 6.06408 4.32042 6.19225 4.19225C6.32042 4.06408 6.4615 4 6.6155 4H14V8H18V12.3845H19V7.5L14.5 3H6.6155C6.15517 3 5.77083 3.15417 5.4625 3.4625C5.15417 3.77083 5 4.15517 5 4.6155V19.3845C5 19.8448 5.15417 20.2292 5.4625 20.5375C5.77083 20.8458 6.15517 21 6.6155 21H11.5V20H6.6155Z" />
    <path d="M12.0002 19.0098C14.6823 19.1715 16.8286 21.3179 16.9903 24H17.0099C17.1717 21.3179 19.318 19.1715 22 19.0098V18.9902C19.318 18.8285 17.1717 16.6822 17.0099 14H16.9903C16.8286 16.6822 14.6823 18.8285 12.0002 18.9902V19.0098Z" />
  </svg>
);

export const ClaimChartIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5.6155 20C5.15517 20 4.77083 19.8458 4.4625 19.5375C4.15417 19.2292 4 18.8448 4 18.3845V5.6155C4 5.15517 4.15417 4.77083 4.4625 4.4625C4.77083 4.15417 5.15517 4 5.6155 4H18.3845C18.8448 4 19.2292 4.15417 19.5375 4.4625C19.8458 4.77083 20 5.15517 20 5.6155V18.3845C20 18.8448 19.8458 19.2292 19.5375 19.5375C19.2292 19.8458 18.8448 20 18.3845 20H5.6155ZM5.6155 19H8.998V15.002H5V18.3845C5 18.564 5.05767 18.7115 5.173 18.827C5.2885 18.9423 5.436 19 5.6155 19ZM9.998 19H14.002V15.002H9.998V19ZM15.002 19H18.3845C18.564 19 18.7115 18.9423 18.827 18.827C18.9423 18.7115 19 18.564 19 18.3845V15.002H15.002V19ZM5 14.002H8.998V9.998H5V14.002ZM9.998 14.002H14.002V9.998H9.998V14.002ZM15.002 14.002H19V9.998H15.002V14.002ZM5 8.998H8.998V5H5.6155C5.436 5 5.2885 5.05767 5.173 5.173C5.05767 5.2885 5 5.436 5 5.6155V8.998ZM9.998 8.998H14.002V5H9.998V8.998ZM15.002 8.998H19V5.6155C19 5.436 18.9423 5.2885 18.827 5.173C18.7115 5.05767 18.564 5 18.3845 5H15.002V8.998Z" />
  </svg>
);

export const ClaimInspectorIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M6.6155 21C6.15517 21 5.77083 20.8458 5.4625 20.5375C5.15417 20.2292 5 19.8448 5 19.3845V4.6155C5 4.15517 5.15417 3.77083 5.4625 3.4625C5.77083 3.15417 6.15517 3 6.6155 3H14.5L19 7.5V11H18V8H14V4H6.6155C6.4615 4 6.32042 4.06408 6.19225 4.19225C6.06408 4.32042 6 4.4615 6 4.6155V19.3845C6 19.5385 6.06408 19.6796 6.19225 19.8078C6.32042 19.9359 6.4615 20 6.6155 20H11V21H6.6155Z" />
    <path d="M18.9 22.15L16.2 19.45C15.85 19.7 15.4625 19.8958 15.0375 20.0375C14.6125 20.1792 14.1667 20.25 13.7 20.25C12.4833 20.25 11.4542 19.8208 10.6125 18.9625C9.77083 18.1042 9.35 17.0667 9.35 15.85C9.35 14.6333 9.77917 13.6 10.6375 12.75C11.4958 11.9 12.5333 11.475 13.75 11.475C14.9667 11.475 16 11.9 16.85 12.75C17.7 13.6 18.125 14.6333 18.125 15.85C18.125 16.3167 18.0542 16.7625 17.9125 17.1875C17.7708 17.6125 17.575 18 17.325 18.35L20.025 21.05L18.9 22.15ZM13.75 19C14.6167 19 15.3542 18.6958 15.9625 18.0875C16.5708 17.4792 16.875 16.7333 16.875 15.85C16.875 14.9833 16.5708 14.2458 15.9625 13.6375C15.3542 13.0292 14.6167 12.725 13.75 12.725C12.8667 12.725 12.1208 13.0292 11.5125 13.6375C10.9042 14.2458 10.6 14.9833 10.6 15.85C10.6 16.7333 10.9042 17.4792 11.5125 18.0875C12.1208 18.6958 12.8667 19 13.75 19Z" />
  </svg>
);

export const DocsIcon = ({ size = 24, ...props }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M8.3845 12.3077H15.6155V11.3077H8.3845V12.3077ZM8.3845 15.077H15.6155V14.077H8.3845V15.077ZM8.3845 17.8462H12.6155V16.8462H8.3845V17.8462ZM6.6155 21C6.15517 21 5.77083 20.8458 5.4625 20.5375C5.15417 20.2292 5 19.8448 5 19.3845V4.6155C5 4.15517 5.15417 3.77083 5.4625 3.4625C5.77083 3.15417 6.15517 3 6.6155 3H14.5L19 7.5V19.3845C19 19.8448 18.8458 20.2292 18.5375 20.5375C18.2292 20.8458 17.8448 21 17.3845 21H6.6155ZM14 8V4H6.6155C6.4615 4 6.32042 4.06408 6.19225 4.19225C6.06408 4.32042 6 4.4615 6 4.6155V19.3845C6 19.5385 6.06408 19.6796 6.19225 19.8078C6.32042 19.9359 6.4615 20 6.6155 20H17.3845C17.5385 20 17.6796 19.9359 17.8077 19.8078C17.9359 19.6796 18 19.5385 18 19.3845V8H14Z" />
  </svg>
);
